import { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { breakpoints, gutters, isMobile, useWindowSize } from 'app/spacing';
import KizenTypography from 'app/kizentypo';
import Loader from 'components/Kizen/Loader';
import { Nav } from 'components/Navigation/NavLayout';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { DesktopOnlyButton } from 'pages/Common/styles/ControlBar';
import { previewSurvey } from 'pages/Surveys';
import { replaceSurvey } from 'store/surveysPage/surveys.redux';
import { setError } from 'store/errors';
import navItems from './navigation';
import BuilderModal from './Builder';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { surveyQuery } from 'query/forms';
import { snakeToCamelCaseKeys } from 'services/helpers';
import { ensureDeserializedFormPages } from 'xforms/api';
import { SurveySettingsPageProvider, useSurveySettingsPage } from './context';

const chosenBusinessUrl = `https://${import.meta.env.VITE_EMBED_FRONTEND_DOMAIN}`;

const MobileContainer = styled.div`
  padding: ${gutters.standard};
  text-align: center;
`;

const Container = styled.div`
  margin-bottom: ${gutters.spacing(4)}px;
`;

const setSurveyTitle = (title, { name = '' } = {}) =>
  name ? `${name} - ${title}` : title;

const Toolbar = ({ routeItems }) => {
  const { t } = useTranslation();
  const params = useParams();
  const { survey, onExportResponses, onShowConfirmDeleteModal } =
    useSurveySettingsPage();

  return (
    <Nav.Container>
      <Nav.TopNavRow>
        <Nav.TopNavLeftContainer>
          <Nav.TopNavBackButton label={t('back to surveys')} path="/surveys" />
        </Nav.TopNavLeftContainer>
        <Nav.TopNavCenterContainer>
          <Nav.TopNavHeader label={survey.name} />
        </Nav.TopNavCenterContainer>
        <Nav.TopNavRightContainer>
          <DesktopOnlyButton onClick={onShowConfirmDeleteModal} color="red">
            {t('Delete')}
          </DesktopOnlyButton>
          <DesktopOnlyButton
            color="blue"
            onClick={() => previewSurvey(chosenBusinessUrl, survey.slug)}
          >
            {t('Preview')}
          </DesktopOnlyButton>
          <DesktopOnlyButton color="blue" onClick={onExportResponses}>
            {t('Export Responses')}
          </DesktopOnlyButton>
        </Nav.TopNavRightContainer>
      </Nav.TopNavRow>
      <Nav.SubNavigationRow>
        {Object.entries(routeItems).map(([key, route], idx, arr) => {
          const { label } = route;
          const common = {
            isLast: idx === arr.length - 1,
            numberOfLinks: arr.length,
            params,
          };

          if (key === 'build') {
            return (
              <Nav.SubNavigationLink
                key={label}
                route={route}
                saveLocationOnNavigate
                {...common}
              />
            );
          }

          return (
            <Nav.SubNavigationLink key={label} route={route} {...common} />
          );
        })}
      </Nav.SubNavigationRow>
    </Nav.Container>
  );
};

export default function SurveySettingsPage({ title }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const [noViewPermissions, setNoViewPermissions] = useState(false);
  const { id } = params;
  const { width } = useWindowSize();
  const mobileDisplay = isMobile(width, breakpoints.lg);
  const preReleaseFeature = usePreReleaseFeatures();

  const query = useQuery(
    surveyQuery(id, {
      gcTime: 0,
      staleTime: 0,
      throwOnError: false,
      select: (data) => {
        const camel = snakeToCamelCaseKeys(data);

        return {
          ...camel,
          formUi: {
            ...camel.formUi,
            pages: ensureDeserializedFormPages(camel.formUi.pages),
          },
        };
      },
    })
  );

  const survey = query.data;

  const routeItems = useMemo(() => {
    const forHide = ['messages', 'rules'];

    return preReleaseFeature
      ? navItems
      : Object.keys(navItems)
          .filter((e) => !forHide.includes(e))
          .reduce((acc, el) => ({ ...acc, [el]: navItems[el] }), {});
  }, [preReleaseFeature]);

  useSetTitleOnLoad(setSurveyTitle(title(t), survey));

  useEffect(() => {
    if (query.isError) {
      const error = query.error;
      if (query.error.isAxiosError) {
        const badPermissions = error.response?.data?.badPermissions;
        if (badPermissions) {
          setNoViewPermissions(true);
        } else {
          dispatch(setError({ code: error?.response?.status }));
        }
      }
    } else if (query.data) {
      dispatch(replaceSurvey({ survey: query.data }));
    }
  }, [query.isError, query.data, query.error, dispatch]);

  const onGoBack = useCallback(() => {
    const formUrl = navItems.settings.path.replace(':id', id);
    if (history.length > 2) {
      history.goBack();
    } else {
      history.push(formUrl);
    }
  }, [history, id]);

  if ((!survey && !noViewPermissions) || (survey && survey.id !== id)) {
    return <Loader loading />;
  }

  const routeComponents = Object.entries(routeItems)
    .filter(([, route]) => !!route.component)
    .map(([, route], index) => (
      <Route
        key={index}
        path={route.path}
        exact={route.exact !== false}
        render={() => (
          <route.component survey={survey} fetchSurvey={query.refetch} />
        )}
      />
    ));

  if (mobileDisplay) {
    return (
      <MobileContainer>
        <KizenTypography>
          {t(
            'This page has not been optimized for mobile. Please revisit on a desktop to receive the best experience.'
          )}
        </KizenTypography>
      </MobileContainer>
    );
  }

  return (
    <SurveySettingsPageProvider survey={survey}>
      <Toolbar routeItems={routeItems} />
      <Container>
        <Switch location={location}>{routeComponents}</Switch>
      </Container>
      {/* react-router way of storing a page behind a modal https://v5.reactrouter.com/web/example/modal-gallery */}
      <Route path="/surveys/:id/build">
        <BuilderModal survey={survey} onGoBack={onGoBack} />
      </Route>
    </SurveySettingsPageProvider>
  );
}
